/* eslint-disable */
import { FeeOptions } from '@uniswap/v3-sdk'
import { Percent } from '@uniswap/sdk-core'
import { PermitSignature } from 'hooks/usePermitAllowance'
import { KyberTrade, TradeFillType } from "state/routing/types";
import { FlatFeeOptions } from '@uniswap/universal-router-sdk'
import { useAccount } from 'hooks/useAccount'
import { CustomUserProperties, SwapEventName } from '@uniswap/analytics-events'
import { useEthersWeb3Provider } from 'hooks/useEthersProvider'
import { useTrace } from 'utilities/src/telemetry/trace/TraceContext'
import useBlockNumber from 'lib/hooks/useBlockNumber'
import { t } from 'i18n'
import { useGetTransactionDeadline } from 'hooks/useTransactionDeadline'
import { useUserSlippageTolerance } from 'state/user/hooks';
import { formatCommonPropertiesForTrade, formatSwapSignedAnalyticsEventProperties } from 'lib/utils/analytics'
import { useTotalBalancesUsdForAnalytics } from 'graphql/data/apollo/TokenBalancesProvider'
import { useCallback } from 'react';
import { BigNumber } from '@ethersproject/bignumber'
import { trace } from 'tracing/trace'
import { didUserReject, swapErrorToUserReadableMessage } from 'utils/swapErrorToUserReadableMessage'

import { sendAnalyticsEvent } from 'uniswap/src/features/telemetry/send'
import { UserRejectedRequestError, WrongChainError } from 'utils/errors'
import { TransactionResponse } from '@ethersproject/abstract-provider'
// import { calculateGasMargin } from 'utils/calculateGasMargin'
import { getWalletMeta } from 'utils/walletMeta'
import { logger } from 'utilities/src/logger/logger'

class GasEstimationError extends Error {
    constructor() {
      super(t('swap.error.expectedToFail'))
    }
}

class ModifiedSwapError extends Error {
constructor() {
    super(t('swap.error.modifiedByWallet'))
    }
}

interface SwapOptions {
    slippageTolerance: Percent
    permit?: PermitSignature
    feeOptions?: FeeOptions
    flatFeeOptions?: FlatFeeOptions
  }


export function useKyberRouterSwapCallback(
    trade: KyberTrade | undefined,
    fiatValues: { amountIn?: number; amountOut?: number; feeUsd?: number },
    options: SwapOptions,
) {
    const account = useAccount()
    const provider = useEthersWeb3Provider()
    const connectorName = useAccount().connector?.name

    const analyticsContext = useTrace()
    const blockNumber = useBlockNumber()
    const getDeadline = useGetTransactionDeadline()
    const isAutoSlippage = useUserSlippageTolerance()[0] === 'auto'
    const portfolioBalanceUsd = useTotalBalancesUsdForAnalytics()
    

    return useCallback(
        (): Promise<{ type: TradeFillType.Kyber; response: TransactionResponse; deadline?: BigNumber}> =>
            trace({ name: 'Swap (Kyber)', op: 'swap.kyber'}, async (trace) => {
                try {
                    if (account.status !== 'connected') {
                        throw new Error('wallet not connected')
                      }
                      if (!provider) {
                        throw new Error('missing provider')
                      }
                      if (!trade) {
                        throw new Error('missing trade')
                      }
                      const connectedChainId = await provider.getSigner().getChainId()
                      if (account.chainId !== connectedChainId) {
                        throw new WrongChainError()
                      }

                      const deadline = await getDeadline()
                      const value = BigNumber.from(trade.inputAmount.currency.isNative ? trade.inputAmount.quotient.toString() : 0)
                      // trace.setData('slippageTolerance', options.slippageTolerance.toFixed(2))

                      const estimateGasOption = {
                        from: account.address,
                        to: trade.routerAddress,
                        data: trade.encodedSwapData,
                        value,
                      }

                      console.log('estimateGasOption:',estimateGasOption)

                      // let gasLimit: BigNumber
                      // try {
                      //   const gasEstimate = await provider.estimateGas(estimateGasOption)
                      //   console.log('gasEstimate', gasEstimate)
                      //   gasLimit = calculateGasMargin(gasEstimate)
                      //   trace.setData('gasLimit', gasLimit.toNumber())
                      // } catch (gasError) {
                      //   console.log('cant estimate gas')
                      //   sendAnalyticsEvent(SwapEventName.SWAP_ESTIMATE_GAS_CALL_FAILED, {
                      //     ...formatCommonPropertiesForTrade(trade, options.slippageTolerance),
                      //     ...analyticsContext,
                      //     client_block_number: blockNumber,
                      //     txRequest: estimateGasOption,
                      //     isAutoSlippage,
                      //   })
                      //   const wrappedError = new Error('gas error', { cause: gasError })
                      //   logger.warn('useKyberRouter', 'useKyberRouterSwapCallback', 'Failed to estimate gas', wrappedError)
                      //   throw new GasEstimationError()
                      // }
                      // const gasPrice = await provider.getGasPrice()
                      const gasLimit = 800000
                     
                      
                      const tx = {
                        from: account.address,
                        to: trade.routerAddress,
                        data: trade.encodedSwapData,
                        gasLimit,
                        ...(value.eq('0') ? {} : { value }),
                      }

                      const response = await trace.child(
                        { name: 'Send transaction', op: 'wallet.send_transaction' },
                        async (walletTrace) => {
                          try {
                            return await provider.getSigner().sendTransaction({ ...tx })
                          } catch (error) {
                            if (didUserReject(error)) {
                              walletTrace.setStatus('cancelled')
                              throw new UserRejectedRequestError(swapErrorToUserReadableMessage(error))
                            } else {
                              throw error
                            }
                          }
                        },
                      )
                      sendAnalyticsEvent(SwapEventName.SWAP_SIGNED, {
                        ...formatSwapSignedAnalyticsEventProperties({
                          trade,
                          timeToSignSinceRequestMs: trace.now(),
                          allowedSlippage: options.slippageTolerance,
                          fiatValues,
                          txHash: response.hash,
                          portfolioBalanceUsd,
                        }),
                        ...analyticsContext,
                        // TODO (WEB-2993): remove these after debugging missing user properties.
                        [CustomUserProperties.WALLET_ADDRESS]: account.address,
                        [CustomUserProperties.WALLET_TYPE]: connectorName,
                        [CustomUserProperties.PEER_WALLET_AGENT]: provider ? getWalletMeta(provider)?.agent : undefined,
                      })
                      if (tx.data !== response.data) {
                        sendAnalyticsEvent(SwapEventName.SWAP_MODIFIED_IN_WALLET, {
                          txHash: response.hash,
                          expected: tx.data || '',
                          actual: response.data,
                          ...analyticsContext,
                        })
                        if (!response.data || response.data.length === 0 || response.data === '0x') {
                          throw new ModifiedSwapError()
                        }
                      }

                      return { type: TradeFillType.Kyber as const, response, deadline }
                } catch (error: unknown) {
                    if (error instanceof GasEstimationError) {
                        throw error
                      } else if (error instanceof UserRejectedRequestError) {
                        trace.setStatus('cancelled')
                        throw error
                      } else if (error instanceof ModifiedSwapError) {
                        trace.setError(error, 'data_loss')
                        throw error
                      } else {
                        trace.setError(error)
                        throw Error(swapErrorToUserReadableMessage(error))
                      }
                }
            }),[
                account.status,
                account.chainId,
                account.address,
                provider,
                trade,
                getDeadline,
                options.slippageTolerance,
                options.permit,
                options.feeOptions,
                options.flatFeeOptions,
                fiatValues,
                portfolioBalanceUsd,
                analyticsContext,
                connectorName,
                blockNumber,
                isAutoSlippage,
            ]
    
    )
}